import { ArrowRight, FileText, MapPin, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { interpolate, useLocale } from "@/i18n/context";
import type { PendingOrder } from "@/lib/pendingOrder";

interface PropertyNextStepProps {
  order: PendingOrder;
  signedIn: boolean;
  fullPrice: string;
  onEdit: () => void;
  onPreview: () => void;
  onPurchase: () => void;
}

/** Confirms the pick, without implying that it has already been verified. */
export function PropertyNextStep({ order, signedIn, fullPrice, onEdit, onPreview, onPurchase }: PropertyNextStepProps) {
  const { t } = useLocale();
  const copy = t.propertyNextStep;
  return (
    <div className="property-next-step motion-safe:animate-fade-in">
      <div className="border-b border-border/70 pb-5">
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="editorial-eyebrow">{copy.eyebrow}</p>
          <Button type="button" variant="ghost" size="sm" onClick={onEdit} className="min-h-11 shrink-0 gap-2">
            <Pencil aria-hidden="true" className="h-3.5 w-3.5" />{copy.edit}
          </Button>
        </div>
        <h2 id="property-next-heading" className="flex items-start gap-2 text-lg font-semibold leading-snug sm:text-2xl">
          <MapPin aria-hidden="true" className="mt-1 h-5 w-5 shrink-0 text-primary" />
          <span className="min-w-0 break-words">{order.address}</span>
        </h2>
        <p className="ml-7 mt-1 text-sm text-muted-foreground">
          {order.propertyType === "stanovanje" ? t.addressSearch.apartment : t.addressSearch.house}
          {order.propertyType === "stanovanje" && order.apartmentNumber && ` · ${t.common.aptNoTitle} ${order.apartmentNumber}`}
        </p>
      </div>
      <div className="grid gap-6 pt-5 md:grid-cols-[1.1fr_1fr] md:gap-8">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold"><FileText aria-hidden="true" className="h-4 w-4 text-primary" />{copy.fullTitle}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{copy.fullNote}</p>
          <Button type="button" size="lg" data-report-choice="full" onClick={onPurchase} className="mt-4 h-auto min-h-12 w-full gap-2 whitespace-normal py-3 sm:w-auto">
            {interpolate(copy.fullCta, { price: fullPrice })}
            <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0" />
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">{copy.packagesNote}</p>
        </div>
        <div className="border-t border-border/70 pt-5 md:border-l md:border-t-0 md:pl-8 md:pt-0">
          <h3 className="text-base font-semibold">{signedIn ? copy.signedInTitle : copy.previewTitle}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{signedIn ? copy.signedInNote : copy.previewNote}</p>
          <Button type="button" variant="outline" size="lg" data-report-choice="preview" onClick={onPreview} className="mt-4 h-auto min-h-12 w-full gap-2 whitespace-normal py-3 sm:w-auto">
            {signedIn ? t.pricing.freePlan.buttonTextLoggedIn : t.pricing.freePlan.buttonText}
            <ArrowRight aria-hidden="true" className="h-4 w-4 shrink-0" />
          </Button>
          {!signedIn && <p className="mt-2 text-xs text-muted-foreground">{copy.noCard}</p>}
        </div>
      </div>
    </div>
  );
}
